import { useContext, useEffect, useState } from 'react';
import * as React from 'react';

import {
  CalciteAvatar,
  CalciteButton,
  CalciteCard,
  CalciteInputText,
  CalciteLabel,
  CalciteModal,
  CalcitePopover,
} from '@esri/calcite-components-react';
import { IUser } from '@esri/arcgis-rest-request';

import StringsContext from '../data/stringsContext';

import { getPortalUrl } from '../util/urlUtils';
import { undefBool } from '../util/webComponentUtils';
import { useAccountManager } from '../contexts/AccountManagerContext';

interface Props {
  layerUrl?: string;
}
function ProfileComponent({ layerUrl }: Props): React.ReactElement {
  const { accountManager, login, logout } = useAccountManager();
  const strings = useContext(StringsContext);
  const [userDetails, setUserDetails] = useState<IUser>();
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
  const [clientId, setClientId] = useState('');
  const [portal, setPortal] = useState<string>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (accountManager) {
      accountManager
        .getUser()
        .then((user) => {
          setUserDetails(user);
          setPopoverVisible(false);
        })
        .catch(() => {});
    } else {
      setUserDetails(undefined);
    }
  }, [accountManager]);

  useEffect(() => {
    if (layerUrl)
      getPortalUrl(layerUrl)?.then((portalUrl) => {
        setPortal(portalUrl);
      });
  }, [layerUrl]);

  const userThumbnail = React.useMemo(() => {
    if (!userDetails || !accountManager) return undefined;
    const { thumbnail } = userDetails;
    const { portal, username, token } = accountManager;

    return thumbnail
      ? `${portal}/community/users/${username}/info/${thumbnail}?token=${token}`
      : undefined;
  }, [accountManager, userDetails]);

  return (
    <>
      <CalciteAvatar
        fullName={userDetails?.fullName}
        username={userDetails?.username}
        thumbnail={userThumbnail}
        className="cursor-pointer"
        onClick={() => setPopoverVisible((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setPopoverVisible((v) => !v);
        }}
        id="profilepopover"
        tabIndex={0}
      />
      {/* Create a portal to the popover to make it available in the dom node */}
      {popoverVisible && (
        <CalcitePopover
          referenceElement="profilepopover"
          label="Account Manager"
          role="dialog"
          placement="auto"
          open={popoverVisible}
          onCalcitePopoverClose={() => setPopoverVisible(false)}
          autoClose
        >
          <CalciteCard>
            <div slot="title">{userDetails?.fullName || 'Sign in'} </div>
            <div slot="subtitle">{userDetails?.username}</div>

            {accountManager && (
              <CalciteButton
                onClick={() => {
                  logout();
                  setPopoverVisible(false);
                }}
                slot="footer-start"
              >
                Sign Out
              </CalciteButton>
            )}
            {!accountManager && (
              <div className="flex flex-col">
                <CalciteButton
                  onClick={() => {
                    login();
                  }}
                  scale="s"
                >
                  ArcGIS Online
                </CalciteButton>
                <CalciteButton
                  onClick={() => {
                    setShowModal(true);
                  }}
                  scale="s"
                  className="pt-2"
                >
                  Enterprise
                </CalciteButton>
              </div>
            )}
          </CalciteCard>
        </CalcitePopover>
      )}
      <CalciteModal open={undefBool(showModal)}>
        <h3 slot="header">{strings.enterpriseSignIn}</h3>
        <div slot="content">
          <CalciteLabel>
            {strings.portalUrl}

            <CalciteInputText
              id="portalUrl"
              placeholder="https://myportal.mydomain.com/portal"
              onCalciteInputTextInput={(e) => setPortal(e.target.value)}
              value={portal ?? ''}
            />
          </CalciteLabel>
          <CalciteLabel>
            {strings.clientId}

            <CalciteInputText
              id="clientId"
              placeholder="e.g. aBcDeFgHi1j2K3L4"
              onCalciteInputTextInput={(e) => setClientId(e.target.value)}
              value={clientId}
            />
          </CalciteLabel>
        </div>
        <CalciteButton
          slot="primary"
          onClick={() =>
            login({
              clientId,
              portal,
            })
          }
        >
          Login
        </CalciteButton>
        <CalciteButton slot="back" onClick={() => setShowModal(false)}>
          Cancel
        </CalciteButton>
      </CalciteModal>
    </>
  );
}

export default ProfileComponent;
